/**
 * Phase 2 AI Features React Component
 * Demonstrates integration of Intelligent Pairing System and Advanced Recommendation Engine
 *
 * This component provides a comprehensive interface for all Phase 2 AI features:
 * - Natural language search
 * - Personalized recommendations
 * - Intelligent pairing suggestions
 * - Conversational AI assistant
 * - Menu optimization insights
 */

import React, { useState, useEffect } from "react";
import "./Phase2AIFeaturesComponent.css";
import {
  AlertCircle,
  Search,
  MessageCircle,
  Utensils,
  TrendingUp,
  Star,
  Brain,
} from "lucide-react";

// Import Phase 2 services
import { phase2IntegrationService } from "../services/phase2IntegrationService";
import { intelligentPairingSystem } from "../services/intelligentPairingSystem";
import { advancedRecommendationEngine } from "../services/advancedRecommendationEngine";

// Simple component replacements
const Card = ({ children, className = "", ...props }) => (
  <div className={`ai-card ${className}`} {...props}>
    {children}
  </div>
);

const CardHeader = ({ children, className = "", ...props }) => (
  <div className={`ai-card-header ${className}`} {...props}>
    {children}
  </div>
);

const CardTitle = ({ children, className = "", ...props }) => (
  <h3 className={`ai-card-title ${className}`} {...props}>
    {children}
  </h3>
);

const CardContent = ({ children, className = "", ...props }) => (
  <div className={`ai-card-content ${className}`} {...props}>
    {children}
  </div>
);

const Button = ({
  children,
  className = "",
  disabled = false,
  onClick,
  ...props
}) => (
  <button
    className={`ai-button ${className}`}
    disabled={disabled}
    onClick={onClick}
    {...props}
  >
    {children}
  </button>
);

const Input = ({ className = "", ...props }) => (
  <input className={`ai-input ${className}`} {...props} />
);

const Badge = ({ children, className = "", variant = "default", ...props }) => (
  <span
    className={`ai-badge ${
      variant === "secondary" ? "ai-badge-secondary" : ""
    } ${className}`}
    {...props}
  >
    {children}
  </span>
);

const Tabs = ({ children, defaultValue, className = "", ...props }) => {
  const [activeTab, setActiveTab] = React.useState(defaultValue);
  return (
    <div className={`ai-tabs ${className}`} {...props}>
      {React.Children.map(children, (child) =>
        React.cloneElement(child, { activeTab, setActiveTab })
      )}
    </div>
  );
};

const TabsList = ({
  children,
  className = "",
  activeTab,
  setActiveTab,
  ...props
}) => (
  <div className={`ai-tabs-list ${className}`} {...props}>
    {React.Children.map(children, (child) =>
      React.cloneElement(child, { activeTab, setActiveTab })
    )}
  </div>
);

const TabsTrigger = ({
  children,
  value,
  className = "",
  activeTab,
  setActiveTab,
  ...props
}) => (
  <button
    className={`ai-tab-trigger ${
      activeTab === value ? "active" : ""
    } ${className}`}
    onClick={() => setActiveTab(value)}
    {...props}
  >
    {children}
  </button>
);

const TabsContent = ({
  children,
  value,
  className = "",
  activeTab,
  ...props
}) => (
  <div
    className={`ai-tab-content ${
      activeTab === value ? "active" : ""
    } ${className}`}
    {...props}
  >
    {children}
  </div>
);

const Phase2AIFeaturesComponent = ({
  inventory = [],
  foodMenu = [],
  userProfile = {},
  restaurantId = null,
}) => {
  // State management
  const [activeTab, setActiveTab] = useState("search");
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [pairingSuggestions, setPairingSuggestions] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [currentMessage, setCurrentMessage] = useState("");
  const [menuOptimization, setMenuOptimization] = useState(null);
  const [systemStatus, setSystemStatus] = useState(null);

  // Initialize component
  useEffect(() => {
    loadSystemStatus();
    if (userProfile && inventory.length > 0) {
      loadPersonalizedRecommendations();
    }
  }, [userProfile, inventory]);

  // Natural Language Search
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    try {
      const results = await advancedRecommendationEngine.naturalLanguageSearch(
        searchQuery,
        inventory,
        { userProfile, occasion: "general" }
      );
      setSearchResults(results);
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults({ error: "Search service temporarily unavailable" });
    }
    setLoading(false);
  };

  // Load personalized recommendations
  const loadPersonalizedRecommendations = async () => {
    if (!userProfile || inventory.length === 0) return;

    try {
      const recs =
        await advancedRecommendationEngine.getPersonalizedRecommendations(
          userProfile,
          inventory,
          { context: "general_browsing" }
        );
      setRecommendations(recs);
    } catch (error) {
      console.error("Recommendations error:", error);
    }
  };

  // Generate pairing suggestions
  const generatePairingSuggestions = async (beverage) => {
    if (!beverage || foodMenu.length === 0) return;

    setLoading(true);
    try {
      const pairings = await intelligentPairingSystem.generatePairings(
        beverage,
        foodMenu,
        { occasion: "dinner", restaurantStyle: "fine_dining" }
      );
      setPairingSuggestions({ beverage, pairings });
    } catch (error) {
      console.error("Pairing error:", error);
      setPairingSuggestions({
        error: "Pairing service temporarily unavailable",
      });
    }
    setLoading(false);
  };

  // Handle conversational interaction
  const handleConversation = async () => {
    if (!currentMessage.trim()) return;

    const userMessage = currentMessage;
    setCurrentMessage("");
    setConversationHistory((prev) => [
      ...prev,
      { type: "user", message: userMessage },
    ]);

    try {
      const response = await phase2IntegrationService.conversationalAssistant({
        message: userMessage,
        history: conversationHistory,
        inventory,
        userProfile,
        context: { restaurantId },
      });

      setConversationHistory((prev) => [
        ...prev,
        {
          type: "assistant",
          message: response.response,
          recommendations: response.recommendations,
          followUp: response.followUp,
        },
      ]);
    } catch (error) {
      console.error("Conversation error:", error);
      setConversationHistory((prev) => [
        ...prev,
        {
          type: "assistant",
          message:
            "I apologize, but I'm having trouble responding right now. Please try again shortly.",
          error: true,
        },
      ]);
    }
  };

  // Load menu optimization
  const loadMenuOptimization = async () => {
    if (inventory.length === 0) return;

    setLoading(true);
    try {
      const optimization =
        await phase2IntegrationService.menuOptimizationSuggestions({
          beverages: inventory,
          foodItems: foodMenu,
          analytics: {
            // This would come from actual analytics in production
            popularItems: [],
            revenue: {},
            averageOrderValue: 0,
          },
        });
      setMenuOptimization(optimization);
    } catch (error) {
      console.error("Menu optimization error:", error);
      setMenuOptimization({
        error: "Optimization service temporarily unavailable",
      });
    }
    setLoading(false);
  };

  // Load system status
  const loadSystemStatus = async () => {
    try {
      const status = phase2IntegrationService.getSystemStatus();
      setSystemStatus(status);
    } catch (error) {
      console.error("System status error:", error);
    }
  };

  // Render search results
  const renderSearchResults = () => {
    if (!searchResults) return null;

    if (searchResults.error) {
      return (
        <Card className="border-red-200">
          <CardContent className="pt-6">
            <div className="flex items-center text-red-600">
              <AlertCircle className="mr-2 h-4 w-4" />
              {searchResults.error}
            </div>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Search className="mr-2 h-5 w-5" />
              Search Results for "{searchResults.query}"
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Found {searchResults.totalFound} beverages matching your search
            </p>

            {searchResults.results.slice(0, 5).map((result, index) => (
              <Card key={index} className="mb-3 p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="font-semibold">{result.beverage.name}</h4>
                    <p className="text-sm text-gray-600">
                      {result.beverage.description}
                    </p>
                    <p className="text-sm mt-2">{result.explanation}</p>
                  </div>
                  <div className="ml-4 text-right">
                    <Badge variant="secondary">
                      {Math.round(result.relevanceScore * 100)}% match
                    </Badge>
                    <p className="text-sm text-gray-600 mt-1">
                      {result.beverage.price}
                    </p>
                    <Button
                      size="sm"
                      className="mt-2"
                      onClick={() =>
                        generatePairingSuggestions(result.beverage)
                      }
                    >
                      Find Pairings
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  };

  // Render recommendations
  const renderRecommendations = () => {
    if (!recommendations) {
      return (
        <Card>
          <CardContent className="pt-6">
            <p className="text-gray-600">
              Loading personalized recommendations...
            </p>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Star className="mr-2 h-5 w-5" />
              Personalized for You
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Based on your preferences •{" "}
              {Math.round(recommendations.confidence * 100)}% confidence
            </p>

            {recommendations.recommendations.slice(0, 6).map((rec, index) => (
              <Card key={index} className="mb-3 p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="font-semibold">{rec.beverage.name}</h4>
                    <p className="text-sm text-gray-600">
                      {rec.beverage.description}
                    </p>
                    <p className="text-sm mt-2 font-medium">{rec.reasoning}</p>
                  </div>
                  <div className="ml-4 text-right">
                    <Badge variant="outline">
                      {Math.round(rec.confidence * 100)}% match
                    </Badge>
                    <p className="text-sm text-gray-600 mt-1">
                      {rec.beverage.price}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  };

  // Render pairing suggestions
  const renderPairingSuggestions = () => {
    if (!pairingSuggestions) return null;

    if (pairingSuggestions.error) {
      return (
        <Card className="border-red-200">
          <CardContent className="pt-6">
            <div className="flex items-center text-red-600">
              <AlertCircle className="mr-2 h-4 w-4" />
              {pairingSuggestions.error}
            </div>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Utensils className="mr-2 h-5 w-5" />
            Pairing Suggestions for {pairingSuggestions.beverage.name}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pairingSuggestions.pairings.slice(0, 4).map((pairing, index) => (
            <Card key={index} className="mb-3 p-4">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h4 className="font-semibold">{pairing.dish.name}</h4>
                  <p className="text-sm text-gray-600">{pairing.rationale}</p>
                </div>
                <Badge variant="outline">
                  {Math.round(pairing.confidence * 100)}% confidence
                </Badge>
              </div>
            </Card>
          ))}
        </CardContent>
      </Card>
    );
  };

  // Render conversation
  const renderConversation = () => {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <MessageCircle className="mr-2 h-5 w-5" />
              AI Sommelier Assistant
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-96 overflow-y-auto mb-4 space-y-3 p-4 bg-gray-50 rounded">
              {conversationHistory.length === 0 ? (
                <p className="text-gray-600 text-center">
                  Ask me anything about our beverage selection!
                </p>
              ) : (
                conversationHistory.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex ${
                      msg.type === "user" ? "justify-end" : "justify-start"
                    }`}
                  >
                    <div
                      className={`max-w-[80%] p-3 rounded-lg ${
                        msg.type === "user"
                          ? "bg-blue-600 text-white"
                          : "bg-white border"
                      }`}
                    >
                      <p>{msg.message}</p>
                      {msg.recommendations &&
                        msg.recommendations.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-200">
                            <p className="text-sm font-medium">
                              Recommendations:
                            </p>
                            {msg.recommendations.slice(0, 2).map((rec, i) => (
                              <p key={i} className="text-sm">
                                • {rec.name}
                              </p>
                            ))}
                          </div>
                        )}
                      {msg.followUp && (
                        <p className="text-sm mt-2 italic">{msg.followUp}</p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="flex space-x-2">
              <Input
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                placeholder="Ask about wine pairings, recommendations, or anything else..."
                onKeyPress={(e) => e.key === "Enter" && handleConversation()}
              />
              <Button
                onClick={handleConversation}
                disabled={!currentMessage.trim()}
              >
                Send
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Render menu optimization
  const renderMenuOptimization = () => {
    if (!menuOptimization) {
      return (
        <Card>
          <CardContent className="pt-6">
            <Button onClick={loadMenuOptimization} disabled={loading}>
              {loading ? "Analyzing..." : "Analyze Menu"}
            </Button>
          </CardContent>
        </Card>
      );
    }

    if (menuOptimization.error) {
      return (
        <Card className="border-red-200">
          <CardContent className="pt-6">
            <div className="flex items-center text-red-600">
              <AlertCircle className="mr-2 h-4 w-4" />
              {menuOptimization.error}
            </div>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <TrendingUp className="mr-2 h-5 w-5" />
              Menu Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="text-center">
                <p className="text-2xl font-bold">{inventory.length}</p>
                <p className="text-sm text-gray-600">Total Beverages</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">
                  {menuOptimization.currentAnalysis?.enhancementStatus
                    ?.percentage || 0}
                  %
                </p>
                <p className="text-sm text-gray-600">AI Enhanced</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">
                  {Math.round(
                    (menuOptimization.currentAnalysis?.varietyScore
                      ?.overallScore || 0) * 100
                  )}
                </p>
                <p className="text-sm text-gray-600">Variety Score</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{foodMenu.length}</p>
                <p className="text-sm text-gray-600">Food Items</p>
              </div>
            </div>

            {menuOptimization.recommendations?.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">
                  Optimization Suggestions:
                </h4>
                {menuOptimization.recommendations
                  .slice(0, 3)
                  .map((rec, index) => (
                    <Card key={index} className="mb-2 p-3">
                      <p className="font-medium">
                        {rec.title || `Recommendation ${index + 1}`}
                      </p>
                      <p className="text-sm text-gray-600">
                        {rec.description || "Optimize menu composition"}
                      </p>
                    </Card>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  // Render system status
  const renderSystemStatus = () => {
    if (!systemStatus) return null;

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Brain className="mr-2 h-5 w-5" />
            AI System Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="font-semibold mb-2">Services</h4>
              <div className="space-y-1">
                {Object.entries(systemStatus.phase2Services || {}).map(
                  ([service, status]) => (
                    <div key={service} className="flex justify-between">
                      <span className="text-sm">
                        {service.charAt(0).toUpperCase() + service.slice(1)}
                      </span>
                      <Badge
                        variant={
                          status === "operational" ? "default" : "destructive"
                        }
                      >
                        {status}
                      </Badge>
                    </div>
                  )
                )}
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Performance</h4>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-sm">Requests</span>
                  <span className="text-sm">
                    {systemStatus.performance?.requestCount || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Success Rate</span>
                  <span className="text-sm">
                    {Math.round(
                      (systemStatus.performance?.successRate || 0) * 100
                    )}
                    %
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Avg Response</span>
                  <span className="text-sm">
                    {Math.round(
                      systemStatus.performance?.averageResponseTime || 0
                    )}
                    ms
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">
          SommelAI Phase 2: Core AI Features
        </h1>
        <p className="text-gray-600">
          Intelligent pairing, personalized recommendations, and conversational
          AI
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="search">Search</TabsTrigger>
          <TabsTrigger value="recommendations">Recommendations</TabsTrigger>
          <TabsTrigger value="pairings">Pairings</TabsTrigger>
          <TabsTrigger value="conversation">AI Assistant</TabsTrigger>
          <TabsTrigger value="optimization">Optimization</TabsTrigger>
        </TabsList>

        <TabsContent value="search" className="mt-6">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Natural Language Search</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex space-x-2 mb-4">
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Try: 'bold red wine for steak' or 'light beer under $15'"
                    onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  />
                  <Button
                    onClick={handleSearch}
                    disabled={loading || !searchQuery.trim()}
                  >
                    {loading ? "Searching..." : "Search"}
                  </Button>
                </div>
                <p className="text-sm text-gray-600">
                  Use natural language to describe what you're looking for
                </p>
              </CardContent>
            </Card>

            {renderSearchResults()}
          </div>
        </TabsContent>

        <TabsContent value="recommendations" className="mt-6">
          {renderRecommendations()}
        </TabsContent>

        <TabsContent value="pairings" className="mt-6">
          <div className="space-y-4">
            {!pairingSuggestions && (
              <Card>
                <CardContent className="pt-6">
                  <p className="text-gray-600">
                    Select a beverage from search results or recommendations to
                    see pairing suggestions
                  </p>
                </CardContent>
              </Card>
            )}

            {renderPairingSuggestions()}
          </div>
        </TabsContent>

        <TabsContent value="conversation" className="mt-6">
          {renderConversation()}
        </TabsContent>

        <TabsContent value="optimization" className="mt-6">
          <div className="space-y-4">
            {renderMenuOptimization()}
            {renderSystemStatus()}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Phase2AIFeaturesComponent;
